
import React, { useContext, useState } from 'react';
import moment from 'moment';
import Swal from 'sweetalert2';
import { AuthContext } from '../../contexts/auth';
import { employeeSignedDocumentDetail } from '../../services/api_dipendenti';
import { estraiMessaggiErrore } from '../../services/utils';
import FilePreviewModal from '../../components/preview_modal';
import Empty from '../../components/empty';
import CustomTooltipComponent from '../../components/custom_tooltip_component';
import {
  ChevronDownIcon,
  ChevronUpIcon,
  EyeIcon,
  DocumentCheckIcon,
  PencilSquareIcon,
} from '@heroicons/react/24/outline';

const BTN_BASE = 'inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-md bg-white text-gray-700 shadow-sm ring-1 ring-inset ring-gray-300 transition hover:bg-indigo-50 hover:text-indigo-700 disabled:cursor-not-allowed disabled:opacity-30';

const formatDate = (value, withTime = false) => {
  if (!value) return '—';
  const date = moment(value);
  return date.isValid()
    ? date.format(withTime ? 'DD/MM/YYYY HH:mm' : 'DD/MM/YYYY')
    : '—';
};

const formatFileSize = bytes => {
  if (bytes == null || !Number.isFinite(Number(bytes))) return '—';
  const size = Number(bytes);
  if (size < 1024) return `${size} B`;
  if (size < 1024 * 1024) return `${(size / 1024).toFixed(1)} KB`;
  return `${(size / (1024 * 1024)).toFixed(2)} MB`;
};


function StatusBadge({ doc }) {
  if (doc.is_signed || doc.employee_signature_status === 'completed') {
    return (
      <span className="inline-flex rounded-full bg-green-100 px-2.5 py-1 text-xs font-semibold text-green-700">
        Firmato
      </span>
    );
  }

  if (doc.status === 'ERROR') {
    return (
      <span className="inline-flex rounded-full bg-red-100 px-2.5 py-1 text-xs font-semibold text-red-700">
        Firma non riuscita
      </span>
    );
  }

  if (doc.status === 'REJECTED') {
    return (
      <span className="inline-flex rounded-full bg-orange-100 px-2.5 py-1 text-xs font-semibold text-orange-700">
        Firma rifiutata
      </span>
    );
  }

  if (doc.employee_signature_status === 'signed_waiting_others') {
    return (
      <div className="flex flex-col items-start gap-1">
        <span className="inline-flex rounded-full bg-green-100 px-2.5 py-1 text-xs font-semibold text-green-800">
          Hai firmato
        </span>
        <span className="text-xs text-gray-500">
          In attesa degli altri firmatari
        </span>
      </div>
    );
  }

  if (doc.employee_signature_status === 'pending') {
    return (
      <span className="inline-flex rounded-full bg-amber-100 px-2.5 py-1 text-xs font-semibold text-amber-800">
        Da firmare
      </span>
    );
  }

  // Stato individuale non verificabile:
  // non dichiara che il dipendente abbia già firmato.
  return (
    <span className="inline-flex rounded-full bg-gray-100 px-2.5 py-1 text-xs font-semibold text-gray-700">
      Firma in corso
    </span>
  );
}

function DetailField({ label, children }) {
  return (
    <div className="grid grid-cols-1 gap-1 py-2.5 sm:grid-cols-12 sm:gap-x-4">
      <dt className="font-semibold text-gray-600 sm:col-span-4">
        {label}
      </dt>
      <dd className="min-w-0 break-words text-gray-800 sm:col-span-8">
        {children}
      </dd>
    </div>
  );
}

export default function ArSignedDocsTable({ docs = [] }) {
  const { token } = useContext(AuthContext);

  const [busyId, setBusyId] = useState(null);
  const [openDocId, setOpenDocId] = useState(null);
  const [detailById, setDetailById] = useState({});
  const [detailErrorId, setDetailErrorId] = useState(null);
  const [previewDoc, setPreviewDoc] = useState(null);

  /* RECUPERO DETTAGLIO DIPENDENTE */
  const getDetail = async doc => {
    if (detailById[doc.id]) return detailById[doc.id];

    const detail = await employeeSignedDocumentDetail(token, doc.id);
    setDetailById(prev => ({
      ...prev,
      [doc.id]: detail,
    }));
    return detail;
  };

  /* APERTURA E CHIUSURA DETTAGLIO */
  const toggleDoc = async doc => {
    if (openDocId === doc.id) {
      setOpenDocId(null);
      return;
    }

    setOpenDocId(doc.id);
    setDetailErrorId(null);

    if (detailById[doc.id]) return;

    setBusyId(doc.id);

    try {
      await getDetail(doc);
    } catch (error) {
      setDetailErrorId(doc.id);
      Swal.fire({
        icon: 'error',
        title: 'Errore',
        text: estraiMessaggiErrore(error),
      });
    } finally {
      setBusyId(null);
    }
  };

  /* VISUALIZZAZIONE PDF */
  const openFile = async (doc, kind) => {
    setBusyId(doc.id);

    try {
      const detail = await getDetail(doc);
      const url = kind === 'signed'
        ? detail.signed_file_url
        : detail.original_file_url;

      if (!url) {
        Swal.fire({
          icon: 'info',
          title: 'File non disponibile',
          text: 'Il documento non è attualmente disponibile per la visualizzazione.',
        });
        return;
      }

      setPreviewDoc({
        url,
        name: kind === 'signed' ? `${doc.name} (firmato)` : doc.name,
        mime: 'application/pdf',
      });
    } catch (error) {
      Swal.fire({
        icon: 'error',
        title: 'Errore',
        text: estraiMessaggiErrore(error),
      });
    } finally {
      setBusyId(null);
    }
  };

  /* FIRMA DOCUMENTO */
  const sign = async doc => {
    setBusyId(doc.id);

    try {
      const detail = await getDetail(doc);
      const url = detail.employee_sign_url;

      if (!url) {
        Swal.fire({
          icon: 'info',
          title: 'Link di firma non disponibile',
          text: 'Utilizza il link che hai ricevuto via email per firmare il documento.',
        });
        return;
      }

      window.open(url, '_blank', 'noopener,noreferrer');
    } catch (error) {
      Swal.fire({
        icon: 'error',
        title: 'Errore',
        text: estraiMessaggiErrore(error),
      });
    } finally {
      setBusyId(null);
    }
  };

  /* AZIONI CONDIVISE DESKTOP E MOBILE */
  const renderActions = doc => {
    const busy = busyId === doc.id;

    return (
      <div className="flex flex-wrap items-center gap-2">
        {doc.has_signed_file ? (
          <CustomTooltipComponent title="Visualizza firmato">
            <button
              type="button"
              disabled={busy}
              onClick={() => openFile(doc, 'signed')}
              className={BTN_BASE}
              aria-label="Visualizza firmato"
            >
              <DocumentCheckIcon className="h-5 w-5" />
            </button>
          </CustomTooltipComponent>
        ) : (
          <CustomTooltipComponent title="Visualizza originale">
            <button
              type="button"
              disabled={busy}
              onClick={() => openFile(doc, 'original')}
              className={BTN_BASE}
              aria-label="Visualizza originale"
            >
              <EyeIcon className="h-5 w-5" />
            </button>
          </CustomTooltipComponent>
        )}

        {!doc.is_signed &&
          doc.employee_signature_status === 'pending' &&
          !['ERROR', 'REJECTED'].includes(doc.status) && (
          <CustomTooltipComponent title="Firma">
            <button
              type="button"
              disabled={busy}
              onClick={() => sign(doc)}
              className={BTN_BASE}
              aria-label="Firma documento"
            >
              <PencilSquareIcon className="h-5 w-5" />
            </button>
          </CustomTooltipComponent>
        )}
      </div>
    );
  };


  /* DETTAGLIO CONDIVISO */
  const renderDetails = doc => {
    const detail = detailById[doc.id] || doc;
    const busy = busyId === doc.id;

    if (busy && !detailById[doc.id]) {
      return (
        <div className="p-4 text-sm text-gray-500">
          Caricamento dettagli...
        </div>
      );
    }

    if (detailErrorId === doc.id) {
      return (
        <div className="p-4 text-sm text-red-600">
          Impossibile caricare il dettaglio del documento.
        </div>
      );
    }

    return (
      <div className="px-4 py-4 text-sm text-gray-700 sm:px-6">
        <dl className="divide-y divide-gray-200">
          <DetailField label="Descrizione">
            {detail.description || '—'}
          </DetailField>

          <DetailField label="Data caricamento">
            {formatDate(detail.created_at, true)}
          </DetailField>

          <DetailField label="Ultimo aggiornamento">
            {formatDate(detail.updated_at, true)}
          </DetailField>

          <DetailField label="Data scadenza">
            {formatDate(detail.due_date)}
          </DetailField>

          <DetailField label="Stato documento">
            <StatusBadge doc={detail} />
          </DetailField>

          {['ERROR', 'REJECTED'].includes(detail.status) && (
            <DetailField label="Errore firma">
              <div
                className="w-full rounded-md border border-red-200 bg-red-50 p-2"
              >
                <p className="text-sm font-semibold text-red-800">
                  {detail.status === 'ERROR'
                    ? 'La procedura di firma non è riuscita.'
                    : 'La firma è stata rifiutata.'}
                </p>

                {detail.error_message && (
                  <p className="text-sm text-red-700">
                    {detail.error_message}
                  </p>
                )}

                {detail.error_code != null && (
                  <p className="text-xs text-red-600">
                    Codice errore: {detail.error_code}
                  </p>
                )}
              </div>
            </DetailField>
          )}

          <DetailField label="Nome file">
            <span className="break-all">
              {detail.file_name || '—'}
            </span>
          </DetailField>

          {detail.file_size != null && (
            <DetailField label="Dimensione file">
              {formatFileSize(detail.file_size)}
            </DetailField>
          )}

          <DetailField label="Visualizza file">
            <div className="flex flex-wrap items-center gap-x-5 gap-y-3">
              {detail.has_signed_file ?? doc.has_signed_file ? (
                <button
                  type="button"
                  disabled={busy}
                  onClick={() => openFile(doc, 'signed')}
                  className="inline-flex items-center gap-1 text-indigo-600 hover:underline disabled:opacity-40"
                >
                  <DocumentCheckIcon className="h-4 w-4" />
                  Firmato
                </button>
              ) : (
                <button
                  type="button"
                  disabled={busy}
                  onClick={() => openFile(doc, 'original')}
                  className="inline-flex items-center gap-1 text-indigo-600 hover:underline disabled:opacity-40"
                >
                  <EyeIcon className="h-4 w-4" />
                  Originale
                </button>
              )}
            </div>
          </DetailField>
        </dl>
      </div>
    );
  };

  if (!docs.length) return <Empty />;

  return (
    <>
      {/* DESKTOP E TABLET */}
      <div className="hidden w-full min-w-0 overflow-hidden rounded-lg border border-gray-300 bg-white shadow-sm md:block">
        <table className="w-full table-fixed">
          <colgroup>
            <col className="w-[6%]" />
            <col className="w-[28%]" />
            <col className="w-[17%]" />
            <col className="w-[15%]" />
            <col className="w-[15%]" />
            <col className="w-[19%]" />
          </colgroup>

          <thead className="border-b border-gray-200 bg-indigo-50">
            <tr>
              <th scope="col" className="px-2 py-3">
                <span className="sr-only">Dettagli</span>
              </th>

              <th scope="col" className="px-3 py-3.5 text-left text-sm font-semibold text-indigo-800">
                Nome
              </th>

              <th scope="col" className="px-3 py-3.5 text-left text-sm font-semibold text-indigo-800">
                Creato il
              </th>

              <th scope="col" className="px-3 py-3.5 text-left text-sm font-semibold text-indigo-800">
                Scadenza
              </th>

              <th scope="col" className="px-3 py-3.5 text-left text-sm font-semibold text-indigo-800">
                Stato
              </th>

              <th scope="col" className="px-3 py-3.5 text-right text-sm font-semibold text-indigo-800">
                Azioni
              </th>
            </tr>
          </thead>

          <tbody className="divide-y divide-gray-200 bg-white">
            {docs.map(doc => {
              const expanded = openDocId === doc.id;

              return (
                <React.Fragment key={doc.id}>
                  <tr
                    className={`cursor-pointer transition-colors ${
                      expanded ? 'bg-indigo-50/40' : 'hover:bg-gray-50'
                    }`}
                    onClick={() => toggleDoc(doc)}
                  >
                    <td className="px-2 py-3 align-middle">
                      <button
                        type="button"
                        aria-label={expanded ? 'Chiudi dettagli' : 'Apri dettagli'}
                        aria-expanded={expanded}
                        onClick={e => {
                          e.stopPropagation();
                          toggleDoc(doc);
                        }}
                        className="rounded p-1 text-gray-500 hover:bg-indigo-100 hover:text-indigo-700"
                      >
                        {expanded
                          ? <ChevronUpIcon className="h-5 w-5" />
                          : <ChevronDownIcon className="h-5 w-5" />}
                      </button>
                    </td>

                    <td className="min-w-0 px-3 py-4 align-middle text-sm">
                      <div className="break-words font-medium text-gray-900">
                        {doc.name}
                      </div>
                    </td>

                    <td className="break-words px-3 py-3.5 align-middle text-sm text-gray-500">
                      {formatDate(doc.created_at, true)}
                    </td>

                    <td className="break-words px-3 py-3.5 align-middle text-sm text-gray-500">
                      {formatDate(doc.due_date)}
                    </td>

                    <td className="px-3 py-3.5 align-middle text-sm">
                      <StatusBadge doc={doc} />
                    </td>

                    <td
                      className="px-3 py-3.5 align-middle text-sm"
                      onClick={e => e.stopPropagation()}
                    >
                      <div className="flex justify-end">
                        {renderActions(doc)}
                      </div>
                    </td>
                  </tr>

                  {expanded && (
                    <tr>
                      <td colSpan={6} className="border-t border-gray-100 bg-gray-50">
                        {renderDetails(doc)}
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* MOBILE - SCHEDE VERTICALI */}
      <div className="w-full min-w-0 space-y-3 md:hidden">
        {docs.map(doc => {
          const expanded = openDocId === doc.id;

          return (
            <div
              key={doc.id}
              className="min-w-0 overflow-hidden rounded-lg border border-gray-300 bg-white shadow-sm"
            >
              {/* INTESTAZIONE */}
              <div className="flex min-w-0 items-start justify-between gap-3 px-4 py-4">
                <div className="min-w-0 flex-1">
                  <div className="break-words text-sm font-semibold text-gray-900">
                    {doc.name}
                  </div>

                  <div className="mt-2">
                    <StatusBadge doc={doc} />
                  </div>
                </div>

                <button
                  type="button"
                  aria-label={expanded ? 'Chiudi dettagli' : 'Apri dettagli'}
                  aria-expanded={expanded}
                  onClick={() => toggleDoc(doc)}
                  className="shrink-0 rounded-md border border-gray-200 bg-white p-1.5 text-gray-500 hover:bg-indigo-50 hover:text-indigo-700"
                >
                  {expanded
                    ? <ChevronUpIcon className="h-5 w-5" />
                    : <ChevronDownIcon className="h-5 w-5" />}
                </button>
              </div>

              {/* INFORMAZIONI */}
              <div className="space-y-3 border-t border-gray-100 bg-gray-50/50 px-4 py-3">
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <span className="text-xs font-medium text-gray-500">
                    Creato il
                  </span>
                  <span className="text-right text-xs text-gray-700">
                    {formatDate(doc.created_at, true)}
                  </span>
                </div>

                <div className="flex flex-wrap items-center justify-between gap-2">
                  <span className="text-xs font-medium text-gray-500">
                    Scadenza
                  </span>
                  <span className="text-right text-xs text-gray-700">
                    {formatDate(doc.due_date)}
                  </span>
                </div>
              </div>

              {/* AZIONI */}
              <div className="flex flex-wrap items-center justify-between gap-3 border-t border-gray-100 px-4 py-3">
                <span className="text-xs font-medium uppercase tracking-wide text-gray-500">
                  Azioni
                </span>
                {renderActions(doc)}
              </div>

              {/* DETTAGLIO ESPANDIBILE */}
              {expanded && (
                <div className="border-t border-gray-200 bg-gray-50">
                  {renderDetails(doc)}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* ANTEPRIMA PDF */}
      <FilePreviewModal
        doc={previewDoc}
        onClose={() => setPreviewDoc(null)}
      />
    </>
  );
}
